import { type SettlementStatus } from '@progear/finance-core';
import { formatEtDate, formatEur, settlementStatusLabel } from '@/lib/cashFlow';

/**
 * Register CSV export (E2) — turn the visible register rows into a spreadsheet the
 * user can open in (Estonian) Excel. PURE: `buildLedgerCsv` only SELECTS + FORMATS
 * the figures finance-core already produced (`formatEur` for every money column,
 * `formatEtDate` for dates) — it does NO money math (L9). The file is `;`-delimited
 * (the Estonian Excel list separator, so the comma decimal in "1 234,56 €" survives)
 * with every field quoted, and a UTF-8 BOM so Excel reads the Estonian letters.
 */

/** The minimal shape one CSV line reads — the register's display fields + raw Money. */
export interface LedgerCsvRow {
  suund: 'income' | 'cost';
  dok: string;
  osapool: string;
  liik: string;
  project: string | null;
  /** Expected (due) date, ISO. */
  tahtaeg: string;
  /** Document (issue) date, ISO, or null. */
  docDate: string | null;
  money: { net: number; vat: number; gross: number };
  /** Document offset covering this document, integer cents, SIGNED in the document's
   *  own direction — prepayment links (ETT↔ARV) plus credit-note netting (KRE↔ARV;
   *  negative for an applied KRE); the effective (netted) gross is `Bruto − Kaetud`.
   *  Resolved by finance-core (`totalDocumentOffsetOf`), never here (L9). */
  kaetud: number;
  /** Paid amount, integer cents. */
  tasutud: number;
  /** Outstanding amount, integer cents. */
  jaak: number;
  status: SettlementStatus;
}

/** The Estonian column headers, in order. */
const HEADERS = [
  'Suund',
  'Dokument',
  'Osapool',
  'Liik',
  'Projekt',
  'Tähtaeg',
  'Dok. kuup.',
  'Neto',
  'KM',
  'Bruto',
  'Kaetud',
  'Tasutud',
  'Jääk',
  'Staatus',
] as const;

/** Quote a CSV field, doubling any embedded quote — always quoted (no per-field
 *  "does this need quoting?" branch), so a comma decimal / `;` / quote is safe. */
const esc = (value: string): string => `"${value.replace(/"/g, '""')}"`;

const row = (cells: string[]): string => cells.map(esc).join(';');

/** Build the CSV text (header + one line per row), CRLF-terminated. Pure. */
export function buildLedgerCsv(rows: readonly LedgerCsvRow[]): string {
  const lines = [row([...HEADERS])];
  for (const r of rows) {
    lines.push(
      row([
        r.suund === 'income' ? 'Tulu' : 'Kulu',
        r.dok,
        r.osapool,
        r.liik,
        r.project ?? '',
        formatEtDate(r.tahtaeg),
        r.docDate ? formatEtDate(r.docDate) : '',
        formatEur(r.money.net),
        formatEur(r.money.vat),
        formatEur(r.money.gross),
        formatEur(r.kaetud),
        formatEur(r.tasutud),
        formatEur(r.jaak),
        settlementStatusLabel(r.status, r.suund),
      ]),
    );
  }
  return lines.join('\r\n');
}

/**
 * Trigger a browser download of the CSV text under `filename`. A thin, branch-free
 * DOM side-effect: a UTF-8 BOM is prepended so Excel detects the encoding, the blob
 * URL is created, clicked through a transient anchor, then revoked.
 */
export function downloadLedgerCsv(filename: string, csv: string): void {
  const blob = new Blob(['\ufeff', csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
